import { useState, useEffect, type ComponentProps } from "react"
import type { VariantProps } from "class-variance-authority"
import {
  PopoverContent as PopoverContentBase,
  PopoverPortal,
} from "@/v2-ui/popover/base/PopoverBase"
import { useQuerySelectorLazy } from "@/v2-ui/utils/utils.dom"
import { useIsMobile } from "@/v2-ui/utils/utils.mobile"
import cn from "@/v2-ui/utils/utils.cn"
import {
  POPOVER_COLLISION_PADDING,
  popoverContentVariants,
} from "./popover.constants"

const DEFAULT_PORTAL_CONTAINER = "#mount"

export type PopoverContentProps = Omit<
  ComponentProps<typeof PopoverContentBase>,
  "collisionBoundary"
> &
  VariantProps<typeof popoverContentVariants> & {
    skipPortal?: boolean
    portalContainer?: string
    collisionBoundary?: Element | string
  }

function PopoverContent(props: PopoverContentProps) {
  const {
    skipPortal,
    size,
    spacing,
    className,
    portalContainer = DEFAULT_PORTAL_CONTAINER,
    collisionBoundary,
    collisionPadding = POPOVER_COLLISION_PADDING,
    ...popoverContentCoreProps
  } = props

  const portalElem = useQuerySelectorLazy(portalContainer)

  const [collisionElem, setCollisionElem] = useState<Element>()

  useEffect(() => {
    if (!collisionBoundary || collisionElem) return

    if (collisionBoundary && typeof collisionBoundary !== "string") {
      setCollisionElem(collisionBoundary)
      return
    }

    let el: Element | null = null

    if (typeof collisionBoundary === "string") {
      el = document.querySelector(collisionBoundary)
    }

    if (el) setCollisionElem(el)
  }, [collisionBoundary, collisionElem])

  const isMobile = useIsMobile()

  const content = (
    <PopoverContentBase
      collisionBoundary={collisionElem}
      collisionPadding={isMobile ? 0 : collisionPadding}
      className={cn(popoverContentVariants({ className, size, spacing }), {
        "!w-screen": isMobile,
      })}
      {...popoverContentCoreProps}
    />
  )

  return skipPortal ? (
    content
  ) : (
    <PopoverPortal container={portalElem}>{content}</PopoverPortal>
  )
}

export default PopoverContent
